/**
 * Conservative session-expiry detection — avoids logging users out on transient
 * network errors, integration 401s, or short getSession timeouts.
 */

const AUTH_SKIP_PATH_FRAGMENTS = [
  '/login',
  '/register',
  '/logout',
  '/session',
  '/google/refresh-token',
  '/passkey',
  '/oauth',
  '/register/oauth',
  '/authorisations/',
  '/integrations',
  // Resource/integration 401s — not app-session failures
  '/social-media/',
];

const SESSION_EXPIRED_MESSAGE =
  /not authenticated|please log in|user not authenticated|session expired|unauthorized.*log in/i;

export type AxiosLikeError = {
  response?: {
    status?: number;
    data?: {
      isAuthenticated?: boolean;
      error?: string;
      message?: string;
      success?: boolean;
    };
  };
  config?: { url?: string; baseURL?: string };
  code?: string;
  message?: string;
};

export function getAxiosRequestPath(error?: AxiosLikeError): string {
  const url = error?.config?.url ?? '';
  const base = error?.config?.baseURL ?? '';
  if (url.startsWith('http')) return url.split('?')[0];
  const combined = `${base.replace(/\/$/, '')}/${url.replace(/^\//, '')}`.replace(
    /\/+/g,
    '/'
  );
  return combined.split('?')[0];
}

export function isAuthSkippedRequestPath(path: string): boolean {
  return AUTH_SKIP_PATH_FRAGMENTS.some((fragment) => path.includes(fragment));
}

export type SessionResponseBody = {
  isAuthenticated?: boolean;
  error?: string;
  message?: string;
  success?: boolean;
};

/** True when the server explicitly reports the login session is gone. */
export function isExplicitUnauthenticatedSession(
  data?: SessionResponseBody | null
): boolean {
  if (!data || typeof data !== 'object') return false;
  if (data.isAuthenticated === false) return true;
  const msg = `${data.error ?? ''} ${data.message ?? ''}`;
  return SESSION_EXPIRED_MESSAGE.test(msg);
}

/** Network/abort/timeouts — never treat as session expiry. */
export function isTransientRequestError(error?: AxiosLikeError): boolean {
  if (!error?.response) return true;
  const code = error.code ?? '';
  const message = error.message ?? '';
  return (
    code === 'ECONNABORTED' ||
    code === 'ERR_CANCELED' ||
    code === 'ERR_NETWORK' ||
    /aborted|timeout|network error/i.test(message)
  );
}

/**
 * Should trigger global logout from an axios error (API interceptors).
 * Ignores integration/resource 401s that are not app-session failures.
 */
export function isDefiniteSessionExpiredAxiosError(
  error?: AxiosLikeError,
  isAuthenticatedInRedux?: boolean
): boolean {
  if (!isAuthenticatedInRedux) return false;
  if (!error || isTransientRequestError(error)) return false;

  const status = error.response?.status;
  const data = error.response?.data;
  const path = getAxiosRequestPath(error);

  if (isAuthSkippedRequestPath(path)) return false;

  if (status === 401 && isExplicitUnauthenticatedSession(data)) {
    return true;
  }

  if (
    status === 403 &&
    isExplicitUnauthenticatedSession(data)
  ) {
    return true;
  }

  return false;
}

/** Result of a session probe (getSession). */
export function isDefiniteSessionProbeFailure(
  sessionResponse: { data?: { isAuthenticated?: boolean } } | null | undefined,
  error?: AxiosLikeError
): boolean {
  if (sessionResponse?.data?.isAuthenticated === false) {
    return true;
  }

  if (!error || isTransientRequestError(error)) {
    return false;
  }

  const status = error.response?.status;
  const data = error.response?.data;

  if (status === 401 && isExplicitUnauthenticatedSession(data)) {
    return true;
  }

  return false;
}
